import { yupResolver } from '@hookform/resolvers/yup';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { TourCategoryModel } from 'api-hooks/tour-category';
import { NavigationRoutes } from 'common/constants';
import { ApiError, ApiResult } from 'common/repositories/common.model';
import { SeparatorVertical } from 'components/styles';
import useDestroyAction from 'hooks/use-destroy-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import Input from 'mantine-components/elements';
import {
  HalfGridContainer,
  OneThirdGridContainer,
} from 'mantine-components/container/grid-container';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { UniskaiFormHeaderRef } from 'templates/form/uniskai-form-header';
import UniskaiFormTemplate from 'templates/form/uniskai-form-template';

import { TourCategoryFormSchema, TourCategoryFormType } from './form-type';
import useTourCategoryDefaultValues from '../hooks/use-tour-category-default-values';

interface TourCategoryFormProps {
  query?: UseQueryResult<ApiResult<TourCategoryModel>, ApiError>;
  onSubmit: (
    input: TourCategoryFormType,
    form: UseFormReturn<TourCategoryFormType>,
  ) => Promise<TourCategoryModel>;
  saveMutation?: UseMutationResult;
}

export default function TourCategoryForm(props: TourCategoryFormProps) {
  const tourCategory = props.query?.data?.data;
  const { t } = useTranslation();
  const defaultValues = useTourCategoryDefaultValues(tourCategory);
  const permission = useGetCrudPermission('tour-categories');
  const { deleteMutate, onDelete } = useDestroyAction('tour-categories');
  const titleRef = React.useRef<UniskaiFormHeaderRef>(null);
  React.useEffect(() => {
    let title = t(`pathname:${NavigationRoutes.TourCategoryCreate}`);
    if (tourCategory) {
      title = t(`pathname:${NavigationRoutes.TourCategoryView}`, {
        extra: tourCategory.name,
      });
    }
    titleRef.current?.setTitle(title);
  }, [t, tourCategory]);

  return (
    <UniskaiFormTemplate
      formProps={{
        defaultEditable: !tourCategory,
        defaultValues,
        resolver: yupResolver(TourCategoryFormSchema()),
        onSubmit: props.onSubmit as any,
      }}
      customs={[
        {
          type: 'delete',
          loading: deleteMutate.isLoading,
          onClick: () => onDelete(tourCategory?.id),
          show: permission.delete,
        },
        {
          type: 'edit',
          show: permission.update,
        },
      ]}
      data={tourCategory}
      query={props.query}
      titleRef={titleRef}
      saveMutation={props.saveMutation}
    >
      <HalfGridContainer>
        <Input
          type="text"
          name="name"
          label={t('common:tour_category')}
          placeholder={t('common:tour_category')}
          required
        />
        <Input
          type="textarea"
          name="description"
          label={t('common:description')}
          placeholder={t('common:description')}
        />
      </HalfGridContainer>
      <SeparatorVertical $margin={16} />
      <OneThirdGridContainer>
        <Input
          type="number"
          name="adminCommission"
          label={t('common:admin_commission')}
          placeholder={t('common:admin_commission')}
          required
        />
        <Input
          type="number"
          name="operatorCommission"
          label={t('common:operator_commission')}
          placeholder={t('common:operator_commission')}
          required
        />
        <Input
          type="number"
          name="salesCommission"
          label={t('common:sales_commission')}
          placeholder={t('common:sales_commission')}
          required
        />
      </OneThirdGridContainer>
    </UniskaiFormTemplate>
  );
}
